import { useState, useEffect, useRef, useCallback } from 'react';
import chatApi, {
  ChatComponent,
  ChatModelOption,
  MatchMeAnswers,
  PendingAction,
  getChatModels,
} from '../services/chatApi';

function genId(prefix: string): string {
  return globalThis.crypto?.randomUUID?.() ?? `${prefix}-${Date.now()}-${Math.random()}`;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  components?: ChatComponent[];
  timestamp: string;
  isProactive?: boolean;
}

interface UseChatOptions {
  onNavigate: (path: string) => void;
  onDataRefresh: () => void;
  initialMessage?: string;
  context?: 'daily' | 'discovery' | 'planner';
  matchMeAnswers?: MatchMeAnswers | null;
}

export interface SoftGateInfo {
  active: boolean;
  type: 'signup' | 'upgrade' | '';
}

export interface UseChatReturn {
  // State
  messages: ChatMessage[];
  isChatExpanded: boolean;
  isChatClosing: boolean;
  inputValue: string;
  isSending: boolean;
  pendingActions: PendingAction[];
  selectedModel: string;
  modelOptions: ChatModelOption[];
  provider: string | null;
  error: string | null;
  softGate: SoftGateInfo;

  // Refs
  messagesEndRef: React.RefObject<HTMLDivElement>;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  chatPanelRef: React.RefObject<HTMLDivElement>;

  // Actions
  setInputValue: (value: string) => void;
  setIsChatExpanded: (expanded: boolean) => void;
  setSelectedModel: (model: string) => void;
  sendMessage: (text: string, opts?: { overrideConversationId?: string | null }) => Promise<void>;
  triggerMessage: (text: string) => void;
  handleConfirmAction: (actionId: string) => Promise<void>;
  handleSelectOption: (option: string) => void;
  handleViewItinerary: (id: number | string) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleCloseChat: () => void;
  handleQuickAction: (action: string) => void;
  addProactiveMessage: (msg: ChatMessage) => void;
  dismissError: () => void;
  hasMessages: boolean;
}

const CONVO_STORAGE_KEY = 'vizday_chat_convo_v1';

export function useChat({ onNavigate, onDataRefresh, initialMessage, context, matchMeAnswers }: UseChatOptions): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  // Resume conversation from localStorage
  const storedConvoId = (() => {
    try { return localStorage.getItem(CONVO_STORAGE_KEY) || null; } catch { return null; }
  })();
  const [conversationId, setConversationIdRaw] = useState<string | null>(storedConvoId);
  const convoRetryRef = useRef(false);

  // Wrap setConversationId to persist to localStorage
  const setConversationId = useCallback((id: string | null) => {
    setConversationIdRaw(id);
    try {
      if (id) localStorage.setItem(CONVO_STORAGE_KEY, id);
      else localStorage.removeItem(CONVO_STORAGE_KEY);
    } catch { /* localStorage unavailable */ }
  }, []);

  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const isSendingRef = useRef(false);
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [isChatClosing, setIsChatClosing] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [modelOptions, setModelOptions] = useState<ChatModelOption[]>([]);
  const [provider, setProvider] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [softGate, setSoftGate] = useState<SoftGateInfo>({ active: false, type: '' });
  const initialMessageSentRef = useRef(false);
  const pendingTriggerRef = useRef<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatPanelRef = useRef<HTMLDivElement>(null);

  // Fetch available models on mount
  useEffect(() => {
    getChatModels().then(({ models, default: defaultModel }) => {
      setModelOptions(models);
      setSelectedModel(prev => prev || defaultModel);
    }).catch(() => {
      // API failed — set empty string; backend uses DEFAULT_CHAT_MODEL
      // But allow sendMessage to proceed (it passes selectedModel to API)
      setSelectedModel(prev => prev || '');
    });
    // Safety: if model fetch takes >3s, unblock by setting empty string
    const timeout = setTimeout(() => {
      setSelectedModel(prev => prev || '');
    }, 3000);
    return () => clearTimeout(timeout);
  }, []);

  // Flush pending trigger message when ready (model loaded or convo cleared for retry)
  useEffect(() => {
    if (pendingTriggerRef.current && !isSending) {
      const msg = pendingTriggerRef.current;
      pendingTriggerRef.current = null;
      // If retrying after invalid convo, force null conversationId
      const opts = convoRetryRef.current ? { overrideConversationId: null as string | null } : undefined;
      sendMessage(msg, opts);
    }
  }, [selectedModel, conversationId, isSending]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to bottom when messages change and chat is expanded
  useEffect(() => {
    if (isChatExpanded) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isChatExpanded]);

  // Focus input when chat expands
  useEffect(() => {
    if (isChatExpanded) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isChatExpanded]);

  // Auto-send initial message once model is loaded
  useEffect(() => {
    if (initialMessage && !initialMessageSentRef.current && selectedModel) {
      initialMessageSentRef.current = true;
      sendMessage(initialMessage);
    }
  }, [initialMessage, selectedModel]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = useCallback(async (text: string, opts?: { overrideConversationId?: string | null }) => {
    if (!text.trim() || isSendingRef.current) return;
    // Block manual sends while a 404 retry is queued
    if (pendingTriggerRef.current) return;
    isSendingRef.current = true;
    const effectiveConvoId = opts && 'overrideConversationId' in opts ? opts.overrideConversationId : conversationId;

    setError(null);
    const userMessage: ChatMessage = {
      id: genId('user'),
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsSending(true);

    try {
      const response = await chatApi.sendMessage(text, effectiveConvoId, selectedModel, context, matchMeAnswers);
      setConversationId(response.conversation_id);
      convoRetryRef.current = false; // reset retry flag on success
      setPendingActions(response.pending_actions);
      if (response.provider) setProvider(response.provider);
      if (response.model) {
        setSelectedModel(response.model);
      }

      const assistantMessage: ChatMessage = {
        id: genId('assistant'),
        role: 'assistant',
        content: response.response.text,
        components: response.response.components,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Check for gate after AI response is added. The backend emits `gate`
      // ('upgrade' at the free-tier cap, 'auth' for anonymous); older responses
      // used `soft_gate`/`gate_type`. Mirror usePlanChat so both chat surfaces
      // honor the same contract. Map to the SoftGateInfo union ('auth'→signup).
      const rawGate = response.gate || (response.soft_gate ? (response.gate_type || 'upgrade') : null);
      if (rawGate) {
        setSoftGate({ active: true, type: rawGate === 'upgrade' ? 'upgrade' : 'signup' });
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : 'Failed to send message';
      // If stored conversation is invalid (404), clear and queue retry
      if (effectiveConvoId && !convoRetryRef.current && (errMsg.includes('404') || errMsg.includes('not found'))) {
        convoRetryRef.current = true;
        setConversationId(null);
        setMessages(prev => prev.slice(0, -1)); // remove the optimistic user message
        // Queue retry for next render when conversationId is null
        pendingTriggerRef.current = text;
        isSendingRef.current = false;
        setIsSending(false);
        return;
      }
      setError(errMsg);
      const errorMessage: ChatMessage = {
        id: genId('error'),
        role: 'system',
        content: 'Sorry, I had trouble processing that. Please try again.',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
      inputRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, selectedModel, matchMeAnswers]);

  const handleConfirmAction = useCallback(async (actionId: string) => {
    if (!conversationId || isSending) return;

    setIsSending(true);
    setError(null);
    try {
      const response = await chatApi.confirmAction(conversationId, actionId);
      setPendingActions(response.pending_actions);
      if (response.provider) setProvider(response.provider);

      const itineraryLink = response.response.components?.find(
        (c: ChatComponent) => c.type === 'itinerary_link'
      );
      if (itineraryLink?.data?.id) {
        onNavigate(`/itinerary/${itineraryLink.data.id}`);
        return;
      }

      const confirmMessage: ChatMessage = {
        id: genId('confirm'),
        role: 'assistant',
        content: response.response.text,
        components: response.response.components,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, confirmMessage]);
      onDataRefresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to confirm action');
      console.error('Failed to confirm action:', err);
    } finally {
      setIsSending(false);
    }
  }, [conversationId, isSending, onNavigate, onDataRefresh]);

  const handleSelectOption = useCallback((option: string) => {
    if (option.startsWith('continue_draft:')) {
      const draftId = option.split(':')[1];
      onNavigate(`/plan/${draftId}`);
      return;
    }
    if (option === 'start_fresh') {
      sendMessage("Start fresh - I don't want to continue any existing drafts");
      return;
    }
    sendMessage(option);
  }, [sendMessage, onNavigate]);

  const handleViewItinerary = useCallback((id: number | string) => {
    onNavigate(`/itinerary/${id}`);
  }, [onNavigate]);

  // Open overlay + queue a message (flush effect sends on next eligible tick)
  const triggerMessage = useCallback((text: string) => {
    setIsChatExpanded(true);
    pendingTriggerRef.current = text;
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    // Clear any pending retry so manual sends aren't blocked
    pendingTriggerRef.current = null;
    sendMessage(inputValue);
  }, [sendMessage, inputValue]);

  const handleCloseChat = useCallback(() => {
    setIsChatClosing(true);
    setTimeout(() => {
      setIsChatExpanded(false);
      setIsChatClosing(false);
    }, 400);
  }, []);

  const handleQuickAction = useCallback((action: string) => {
    // Clear any pending retry so quick actions aren't blocked
    pendingTriggerRef.current = null;
    sendMessage(action);
  }, [sendMessage]);

  const addProactiveMessage = useCallback((msg: ChatMessage) => {
    setMessages(prev => {
      if (prev.length === 0) return [msg];
      return prev;
    });
  }, []);

  return {
    messages,
    isChatExpanded,
    isChatClosing,
    inputValue,
    isSending,
    pendingActions,
    selectedModel,
    modelOptions,
    provider,
    error,
    softGate,
    messagesEndRef,
    inputRef,
    chatPanelRef,
    setInputValue,
    setIsChatExpanded,
    setSelectedModel,
    sendMessage,
    triggerMessage,
    handleConfirmAction,
    handleSelectOption,
    handleViewItinerary,
    handleSubmit,
    handleCloseChat,
    handleQuickAction,
    addProactiveMessage,
    dismissError: useCallback(() => setError(null), []),
    hasMessages: messages.length > 0,
  };
}
